import * as _ from 'lodash';
import { cloneElement, useMemo } from 'react';
import type { FunctionComponent, ReactElement } from 'react';

import Locator from '../../../../shared/Locator';
import { isDependencyCacheValid } from '../../../helpers/revisions';
import { getProjectBaseURLFromParams } from '../../../utils';
import Link from '../../generic/Link/Link';
import List from '../../generic/List';
import type { ProjectGroupRelease, Revision } from '../../../types';

type Props = {
  //  This is passed down from ProjectGroupContainer, and on to the dependency pages. It is undefined while it loads.
  projectGroupRelease?: ProjectGroupRelease;
};

const ProjectGroupDependencyValidityContainer: FunctionComponent<Props> = ({
  children,
  ...props
}) => {
  const { projectGroupRelease } = props;
  const content = useMemo(() => {
    if (!projectGroupRelease) {
      return null;
    }

    const invalidProjects = projectGroupRelease.projects.filter((project) =>
      !isDependencyCacheValid(project.revision as Revision),
    );

    const isAllDependencyCacheValid = _.isEmpty(invalidProjects);

    if (isAllDependencyCacheValid) {
      return cloneElement(children as ReactElement, props);
    }

    const minimalInvalidProjects = invalidProjects.map((project) => {
      const locator = new Locator(project.revisionId);
      const revisionLink = getProjectBaseURLFromParams({
        project_id: project.projectId,
        reference_type: 'branch',
        reference: project.branch,
        revision_id: locator.revision.value as string,
      });
      return {
        projectLocator: project.projectId,
        projectTitle: project.project.title,
        revisionLink,
      };
    });

    return (
      <div className="container container-responsive">
        <div className="margin-top">
          <div className="alert alert-warning">
            Please wait while FOSSA determines the dependencies for all revisions, this step can take a few minutes to
            complete...
          </div>
          <div>
            <List>
              <List.Header>
                <List.HeaderText>
                  The following projects are still being analyzed:
                </List.HeaderText>
              </List.Header>
              {minimalInvalidProjects.map((project) => (
                <List.Row key={project.projectLocator}>
                  <Link to={project.revisionLink}>
                    {project.projectTitle || project.projectLocator}
                  </Link>
                </List.Row>
              ))}
            </List>
          </div>
        </div>
      </div>
    );
  }, [children, props, projectGroupRelease]);

  return content;
};

export default ProjectGroupDependencyValidityContainer;
